import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext'
import { replace, useNavigate, Navigate } from 'react-router-dom';
import { getApgJWT } from '../getApgJWT';
import {
    Container,
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    Alert,
    CircularProgress,
    Stack,
    InputAdornment,
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LockIcon from '@mui/icons-material/Lock';
import AppsIcon from '@mui/icons-material/Apps';
import { loginUser } from '../api/api';
import { decryptAES, encryptAES, decryptAESYR, encryptAESYR } from '../cryptoUtils'

// --- Elevated Flat Brand Palette (Consistent across files) ---
const BRAND_CTA_ACCENT = '#e77817'; // Orange/Accent
const BRAND_HEADER_ACCENT = '#97291e'; // Dark Red/Maroon
const BRAND_PRIMARY_TEXT = '#053c6d'; // Dark Blue/Navy
const UI_CANVAS_BACKGROUND = '#f7f7f7'; // Light screen background
const UI_CARD_BACKGROUND = '#ffffff'; // White background for the login box
const UI_SHADOW = '0 4px 12px rgba(0, 0, 0, 0.08)'; // Modern, subtle shadow

const LoginPage = () => {
    const [ntid, setNtid] = useState('');
    const [password, setPassword] = useState('');
    const [appNumber, setAppNumber] = useState('');
    
    // New states for input validation errors
    const [ntidError, setNtidError] = useState('');
    const [appNumberError, setAppNumberError] = useState('');

    const [error, setError] = useState(''); // General API or login failure error
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const { loginSSO, loginManual, isAuthenticated } = useAuth();
    const ssoCalled = useRef(false)
    // Helper function for input validation

    // SSO Effect (Unchanged, but uses the centralized loading state)
    useEffect(() => {
        let params = new URLSearchParams(window.location.search);
        let ssoKey = params.get('key'); 
        
        if (ssoKey) {
            ssoKey = ssoKey.replace(/ /g, '+');
        }
        
        if (!ssoKey || ssoCalled.current) return;
        
        setLoading(true); 
        ssoCalled.current = true;

        (async () => {
            try {
                // 1. Split URL parameter into 'abc' and 'pqr' safely
                const firstPipeIndex = ssoKey.indexOf('|');
                if (firstPipeIndex === -1) throw new Error("Invalid SSO Key format");

                const encGenerateKeyResponse = ssoKey.substring(0, firstPipeIndex); // Encrypted JSON
                console.log("Decrypting first part of the key: ", encGenerateKeyResponse)
                console.log("Successful, First Part: ", decryptAES(encGenerateKeyResponse))
                const encappNo = ssoKey.substring(firstPipeIndex + 1); 
                console.log("Decrypting second part of the key: ", encappNo)
                console.log("Successful, App Number: ", decryptAES(encappNo))
                const generateKeyResponse = decryptAES(encGenerateKeyResponse);
                if (!generateKeyResponse) throw new Error("Failed to decrypt abc payload");
                
                const parsedGenerateKeyResponse = JSON.parse(generateKeyResponse);
                const innerKey = decryptAES(parsedGenerateKeyResponse.key); // e.g. "adtsdcvsdv|asedfvsdcvsdc"
                console.log("Inner Key: ", innerKey)

                const firstPipeIndexKey = innerKey.indexOf('|')
                if (firstPipeIndexKey === -1) throw new Error("Invalid SSO Key format");
                const ntid = decryptAESYR(innerKey.substring(0, firstPipeIndexKey))
                console.log("Ntid: ", ntid)

                const initialToken = parsedGenerateKeyResponse['jwt-token']; // e.g. "eyascbaosljkd..."
                console.log("Initial Token: ", initialToken)
                // console.log("Actual Key: ", innerKey)
                const appNo = decryptAES(encappNo)
                const encappNoYR = encryptAESYR(appNo)
                const payloadKey = `${innerKey}|${encappNoYR}`;
                console.log("Final Payload: ", payloadKey)

                // 4. Extract User ID and App Number for the React Context State
                // (Assuming the first part of innerKey is the encrypted NTID, like in your old code)
                const encUserId = innerKey.split('|')[0];
                const userId = decryptAESYR(encUserId) || '';

                if (!userId) {
                    throw new Error("Failed to extract User ID.");
                }

                const response = await loginSSO(payloadKey, initialToken, ntid, appNo);
                console.log("SSO Response: ", response);
                
                
                // login({'ntid': userId}, sessionId, appNo);
            //     navigate('/chat', 
            //     { state: { 
            //         ntid: userId 
            //     },
            //     replace: true
            // });
                
            } catch (e) {
                console.error('SSO login failed:', e);   
                setError('Automatic login failed. Please use manual credentials.');
            } finally {
                setLoading(false);
                // Wipe the key from the URL so it's clean and safe
                // const cleanUrl = `${window.location.origin}${window.location.pathname}`;
                // window.history.replaceState({}, document.title, cleanUrl);
            }
        })();
    }, [loginSSO]);
    if (isAuthenticated) {
        return <Navigate to="/chat" replace />;
    }
    // Handle input changes with validation
    const handleNtidChange = (e) => {
        const value = e.target.value;
        setNtid(value);
        //setNtidError(validateInput(value, 'NTID'));
        setError(''); // Clear general error on input change
    };

    const handleAppNumberChange = (e) => {
        const value = e.target.value;
        setAppNumber(value);
        //setAppNumberError(validateInput(value, 'App Number'));
        setError(''); // Clear general error on input change
    };

    // Password field doesn't get strict "no special char" validation for usability
    const handlePasswordChange = (e) => {
        setPassword(e.target.value);
        setError(''); // Clear general error on input change
    };

    // Manual Flow
    const handleLogin = async (e) => {
        e.preventDefault();
        try {
            const response = await loginUser(ntid, password, appNumber);
            
            // Pass the typed IDs INTO the context!
            await loginManual(response, ntid, appNumber);
            
            // NO navigate() needed here!
        } catch (err) {
            // handle error
        }
    };

    // Style for modern TextFields (Outlined, using brand colors for focus)
    const modernTextFieldStyles = {
        mt: 2,
        '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            transition: 'border-color 0.3s',
            '& fieldset': { borderColor: '#ccc' },
            '&.Mui-focused fieldset': { 
                borderColor: BRAND_PRIMARY_TEXT, 
                borderWidth: '2px',
            },
        },
        '& .MuiInputLabel-root': { color: BRAND_PRIMARY_TEXT },
        '& .MuiSvgIcon-root': { color: BRAND_PRIMARY_TEXT },
    };

    return (
        <Container
            component="main"
            maxWidth="xs"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                bgcolor: UI_CANVAS_BACKGROUND,
                p: 2, 
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    backgroundColor: UI_CARD_BACKGROUND, 
                    boxShadow: UI_SHADOW, 
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    borderRadius: 3, 
                    width: '100%',
                    overflow: 'hidden',
                }}
            >
                {/* --- ORANGE ACCENT BAR --- */}
                <Box
                    sx={{
                        width: '100%',
                        height: 6,
                        backgroundColor: BRAND_CTA_ACCENT
                    }}
                />

                <Box sx={{ padding: { xs: 3, sm: 4 }, width: '100%' }}>

                    {/* Header Section */}
                    <Stack alignItems="center" spacing={0.5} sx={{ mb: 4, mt: 1 }}>
                        <Typography
                            component="h1"
                            variant="h5"
                            sx={{
                                fontWeight: 700,
                                color: BRAND_HEADER_ACCENT,
                            }}
                        >
                            Welcome to InstaTrack
                        </Typography>
                        <Typography variant="caption" color={BRAND_PRIMARY_TEXT}>
                            Designed by BIU
                        </Typography>
                    </Stack>

                    <Box component="form" onSubmit={handleLogin} sx={{ width: '100%' }}>
                        {(error || ntidError || appNumberError) && (
                            <Alert 
                                severity="error" 
                                sx={{ mb: 2, borderRadius: 1 }}
                            >
                                {error || ntidError || appNumberError}
                            </Alert>
                        )}
                        
                        {/* NTID Input */}
                        <TextField
                            variant="outlined"
                            required
                            fullWidth
                            id="ntid"
                            label="NTID"
                            name="ntid"
                            autoFocus
                            value={ntid}
                            onChange={handleNtidChange} // Use new handler
                            error={!!ntidError} // Set error state for MUI component
                            helperText={ntidError ? 'Only letters and numbers are permitted.' : ''}
                            sx={modernTextFieldStyles}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AccountCircle />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Password Input */}
                        <TextField
                            variant="outlined"
                            required
                            fullWidth
                            name="password"
                            label="Password"
                            type="password"
                            id="password"
                            value={password}
                            onChange={handlePasswordChange} // Use new handler
                            sx={modernTextFieldStyles}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <LockIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        
                        {/* App Number Input */}
                        <TextField
                            variant="outlined"
                            required
                            fullWidth
                            id="appNumber"
                            label="App Number"
                            name="appNumber"
                            value={appNumber}
                            onChange={handleAppNumberChange} // Use new handler
                            error={!!appNumberError} // Set error state for MUI component
                            helperText={appNumberError ? 'Only letters and numbers are permitted.' : ''}
                            sx={modernTextFieldStyles}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <AppsIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Submit Button */}
                        <Button
                            type="submit"
                            fullWidth
                            disabled={loading}
                            variant="contained"
                            sx={{
                                mt: 4,
                                py: 1.5,
                                borderRadius: '8px',
                                backgroundColor: BRAND_CTA_ACCENT,
                                color: '#FFFFFF',
                                fontWeight: 600,
                                '&:hover': {
                                    backgroundColor: '#d66a12',
                                    boxShadow: '0 6px 15px rgba(231, 120, 23, 0.3)',
                                },
                            }}
                        >
                            {loading ? <CircularProgress size={24} sx={{ color: '#fff' }} /> : 'Sign In'}
                        </Button>
                    </Box>
                </Box>

                {/* Footer Section */}
                <Typography variant="caption" align="center" color={BRAND_PRIMARY_TEXT} sx={{ mt: 2, mb: 2, px: 2 }}>
                    © 2026 ICICI Prudential Life Insurance Company Ltd. All rights reserved.
                </Typography>
            </Paper>
        </Container>
    );
};

export default LoginPage;